<template>
  <div class="historial-carta">
    <div class="historial-carta__header">
      <h2 class="historial-carta__titulo">
        Historial de carta
        <span class="historial-carta__rango" v-if="rango">
          Del {{ fmtFecha(rango.desde) }} al {{ fmtFecha(rango.hasta) }}
        </span>
      </h2>
      <SelectorPeriodo :dias="30" @cambio="alCambiarPeriodo" />
    </div>

    <div v-if="cargando" class="historial-carta__cargando">Cargando historial…</div>

    <template v-else>
      <div class="historial-carta__toolbar">
        <base-input
          v-model="busqueda"
          prepend-icon="fas fa-search"
          placeholder="Buscar por usuario, rol o plato..."
          class="historial-carta__buscar"
        ></base-input>
        <base-button type="success" size="sm" @click="descargar" :disabled="!cambiosFiltrados.length">
          <i class="fas fa-download"></i> Descargar Excel
        </base-button>
      </div>

      <!--
        Filtros con el mismo lenguaje visual del dashboard de Operaciones:
        selects con contador y segmented control en negro para el activo.
        El filtrado es todo en cliente, sobre las filas ya cargadas.
      -->
      <div class="filtros">
        <div class="filtros__campo">
          <select v-model="filtros.categoria" class="filtros__select">
            <option :value="null">Categoría: todas ({{ cambios.length }})</option>
            <option v-for="o in opcionesCategoria" :key="o.valor" :value="o.valor">
              {{ o.etiqueta }} ({{ o.n }})
            </option>
          </select>
          <i class="fas fa-chevron-down filtros__chevron"></i>
        </div>

        <div class="filtros__campo">
          <select v-model="filtros.usuario" class="filtros__select">
            <option :value="null">Usuario: todos</option>
            <option v-for="o in opcionesUsuario" :key="o.valor" :value="o.valor">
              {{ o.etiqueta }} ({{ o.n }})
            </option>
          </select>
          <i class="fas fa-chevron-down filtros__chevron"></i>
        </div>

        <div class="filtros__campo">
          <select v-model="filtros.rol" class="filtros__select">
            <option :value="null">Rol: todos</option>
            <option v-for="o in opcionesRol" :key="o.valor" :value="o.valor">
              {{ o.etiqueta }} ({{ o.n }})
            </option>
          </select>
          <i class="fas fa-chevron-down filtros__chevron"></i>
        </div>

        <div class="segmented">
          <button
            v-for="o in opcionesAccion"
            :key="o.valor === null ? 'todas' : o.valor"
            type="button"
            class="segmented__item"
            :class="{ 'segmented__item--activo': filtros.accion === o.valor }"
            @click="filtros.accion = o.valor"
          >
            {{ o.etiqueta }} ({{ o.n }})
          </button>
        </div>

        <button v-if="hayFiltros" type="button" class="filtros__limpiar" @click="limpiarFiltros">
          <i class="fas fa-times"></i> Limpiar filtros
        </button>
      </div>

      <el-table :data="cambiosPaginados" size="small" stripe empty-text="Sin cambios de carta registrados en el periodo">
        <el-table-column label="Fecha" width="110" align="center" header-align="center">
          <template slot-scope="s">{{ fmtSoloFecha(s.row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="Hora" width="90" align="center" header-align="center">
          <template slot-scope="s">{{ fmtSoloHora(s.row.created_at) }}</template>
        </el-table-column>
        <el-table-column prop="user_name" label="Usuario" width="180" align="center" header-align="center"></el-table-column>
        <el-table-column label="Rol" width="120" align="center" header-align="center">
          <template slot-scope="s">{{ s.row.user_role || '—' }}</template>
        </el-table-column>
        <el-table-column label="Plato" width="265" align="center" header-align="center">
          <template slot-scope="s">{{ s.row.meal_name || ('Plato #' + s.row.meal_numeric_id) }}</template>
        </el-table-column>
        <el-table-column label="Categoría" width="175" align="center" header-align="center">
          <template slot-scope="s">
            <span :class="{ 'celda-vacia': !s.row.meal_category }">
              {{ s.row.meal_category || '—' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="Acción" width="130" align="center" header-align="center">
          <template slot-scope="s">
            <span class="pill" :class="s.row.new_status ? 'pill--verde' : 'pill--roja'">
              {{ s.row.new_status ? 'Encendió' : 'Apagó' }}
            </span>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        v-if="cambiosFiltrados.length > porPagina"
        class="historial-carta__paginacion"
        layout="total, prev, pager, next"
        :total="cambiosFiltrados.length"
        :page-size="porPagina"
        :current-page.sync="paginaActual"
      ></el-pagination>

      <!--
        El backend corta en `tope` filas (las mas recientes). Sin este aviso la
        lista se ve completa y no lo esta: alguien podria concluir "no hubo mas
        cambios" cuando en realidad quedaron afuera.
      -->
      <p v-if="truncado" class="historial-carta__aviso">
        Se muestran los <strong>{{ tope }}</strong> cambios más recientes del período.
        Hubo más, pero no se listan. Acotá el rango de fechas para verlos.
      </p>

    </template>
  </div>
</template>

<script>
import { Table, TableColumn, Pagination } from 'element-ui'
import SelectorPeriodo from '@/components/Metrics/SelectorPeriodo.vue'
import CartaStatusGateway from '@/gateways/CartaStatusGateway'
import { descargarExcel } from '@/utils/excel'

/**
 * Quien apago/prendio que plato de la carta OD, para la cocina del catering
 * autenticado. Complementa la card "% Carta activa": la card mide cuanto
 * tiempo estuvo disponible la carta, esta lista muestra que cuenta hizo cada
 * cambio (control interno del dueño sobre admins).
 *
 * Nunca envia una cocina: el backend la resuelve del token, y el historial
 * solo trae filas de esa cocina.
 */
export default {
  name: 'HistorialCarta',
  components: {
    SelectorPeriodo,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Pagination.name]: Pagination
  },
  data () {
    return {
      // Los pone SelectorPeriodo al montarse, antes de la primera carga.
      desde: null,
      hasta: null,
      cargando: true,
      cambios: [],
      // Cuentas reales del catering (las trae el backend de user_caterings).
      // Se usan para que el filtro liste tambien a quien no toco nada.
      usuarios: [],
      rango: null,
      // Tope de filas que devuelve el backend. Viene en la respuesta en vez de
      // estar duplicado aca: si cambia alla, el aviso lo sigue solo.
      tope: null,
      busqueda: '',
      filtros: { categoria: null, usuario: null, rol: null, accion: null },
      paginaActual: 1,
      porPagina: 20
    }
  },
  computed: {
    /*
      El backend devuelve como mucho `tope` filas. Si llegaron justo esas, hay
      que asumir que quedaron cambios afuera — no hay forma de distinguir "hubo
      exactamente 500" de "se corto en 500", y callarlo es peor que avisar de mas.
    */
    truncado () {
      return this.tope > 0 && this.cambios.length >= this.tope
    },
    cambiosFiltrados () {
      return this.aplicar(this.cambios, this.filtros)
    },
    /*
      Contadores "facetados": cada filtro cuenta sobre las filas que pasan
      TODOS LOS DEMAS filtros, no sobre el total. Asi el numero al lado de una
      opcion es lo que vas a ver si la eliges — nunca "Bowls (5)" que al
      clickear devuelve 0 porque otro filtro ya los descarto.
    */
    opcionesCategoria () {
      return this.contar('categoria', c => c.meal_category || 'Sin categoría')
    },
    opcionesUsuario () {
      const base = this.contar('usuario', c => c.user_name)
      const vistos = new Set(base.map(o => o.valor))
      // Cuentas del catering que no aparecen en el log: se listan en 0. Que un
      // admin nunca haya apagado un plato tambien es dato para el dueño.
      const sinActividad = this.usuarios
        .filter(u => u.name && !vistos.has(u.name))
        .map(u => ({ valor: u.name, etiqueta: u.name, n: 0 }))
      return base.concat(sinActividad.sort((a, b) => a.etiqueta.localeCompare(b.etiqueta)))
    },
    opcionesRol () {
      return this.contar('rol', c => c.user_role || 'Sin rol')
    },
    opcionesAccion () {
      const sinAccion = { ...this.filtros, accion: null }
      const filas = this.aplicar(this.cambios, sinAccion)
      return [
        { valor: null, etiqueta: 'Todas', n: filas.length },
        { valor: 'on', etiqueta: 'Encendió', n: filas.filter(c => c.new_status).length },
        { valor: 'off', etiqueta: 'Apagó', n: filas.filter(c => !c.new_status).length }
      ]
    },
    hayFiltros () {
      const f = this.filtros
      return !!(f.categoria || f.usuario || f.rol || f.accion || this.busqueda.trim())
    },
    cambiosPaginados () {
      const inicio = (this.paginaActual - 1) * this.porPagina
      return this.cambiosFiltrados.slice(inicio, inicio + this.porPagina)
    }
  },
  watch: {
    busqueda () {
      this.paginaActual = 1
    },
    filtros: {
      deep: true,
      handler () { this.paginaActual = 1 }
    }
  },
  methods: {
    /* SelectorPeriodo emite tambien al montarse: esa es la primera carga. */
    alCambiarPeriodo (rango) {
      this.desde = rango.desde
      this.hasta = rango.hasta
      this.cargar()
    },
    /**
     * Filtrado puro: predicados AND encadenados, null = "sin filtro".
     * Mismo modelo que filterTickets() del dashboard de Operaciones.
     */
    aplicar (filas, f) {
      const q = (this.busqueda || '').trim().toLowerCase()

      return filas.filter(c => {
        if (f.categoria && (c.meal_category || 'Sin categoría') !== f.categoria) return false
        if (f.usuario && c.user_name !== f.usuario) return false
        if (f.rol && (c.user_role || 'Sin rol') !== f.rol) return false
        if (f.accion === 'on' && !c.new_status) return false
        if (f.accion === 'off' && c.new_status) return false

        if (q) {
          const plato = c.meal_name || ('Plato #' + c.meal_numeric_id)
          const accion = c.new_status ? 'encendió' : 'apagó'
          const campos = [c.user_name, c.user_role, plato, c.meal_category, accion]
          if (!campos.some(v => (v || '').toLowerCase().includes(q))) return false
        }

        return true
      })
    },
    /** Opciones + contador de un filtro, ignorando ese mismo filtro. */
    contar (clave, valorDe) {
      const filas = this.aplicar(this.cambios, { ...this.filtros, [clave]: null })
      const cuenta = new Map()
      for (const c of filas) {
        const v = valorDe(c)
        if (!v) continue
        cuenta.set(v, (cuenta.get(v) || 0) + 1)
      }
      return [...cuenta.entries()]
        .map(([valor, n]) => ({ valor, etiqueta: valor, n }))
        .sort((a, b) => b.n - a.n || a.etiqueta.localeCompare(b.etiqueta))
    },
    limpiarFiltros () {
      this.filtros = { categoria: null, usuario: null, rol: null, accion: null }
      this.busqueda = ''
    },
    fmtFecha (iso) {
      if (!iso) return ''
      const p = iso.split('-')
      return p[2] + '/' + p[1] + '/' + p[0]
    },
    fmtSoloFecha (ts) {
      if (!ts) return '—'
      const d = new Date(ts)
      return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    fmtSoloHora (ts) {
      if (!ts) return '—'
      const d = new Date(ts)
      return d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
    },
    descargar () {
      descargarExcel('historial-carta', [{
        nombre: 'Historial de carta',
        columnas: [
          { clave: 'fecha', titulo: 'Fecha', ancho: 12, tipo: 'texto' },
          { clave: 'hora', titulo: 'Hora', ancho: 10, tipo: 'texto' },
          { clave: 'usuario', titulo: 'Usuario', ancho: 24, tipo: 'texto' },
          { clave: 'rol', titulo: 'Rol', ancho: 16, tipo: 'texto' },
          { clave: 'plato', titulo: 'Plato', ancho: 40, tipo: 'texto' },
          { clave: 'categoria', titulo: 'Categoría', ancho: 20, tipo: 'texto' },
          { clave: 'accion', titulo: 'Acción', ancho: 12, tipo: 'texto' }
        ],
        filas: this.cambiosFiltrados.map(c => ({
          fecha: this.fmtSoloFecha(c.created_at),
          hora: this.fmtSoloHora(c.created_at),
          usuario: c.user_name,
          rol: c.user_role || '',
          plato: c.meal_name || ('Plato #' + c.meal_numeric_id),
          categoria: c.meal_category || '',
          accion: c.new_status ? 'Encendió' : 'Apagó'
        }))
      }])
    },
    cargar () {
      this.cargando = true
      this.paginaActual = 1


      CartaStatusGateway.historial(this.desde, this.hasta)
        .then(payload => {
          const r = payload.result || {}
          this.cambios = r.cambios || []
          this.usuarios = r.usuarios || []
          this.rango = r.rango || null
          this.tope = r.tope || null
        })
        .catch(() => { this.cambios = []; this.usuarios = []; this.tope = null })
        .finally(() => { this.cargando = false })
    }
  }
}
</script>

<style scoped>
.historial-carta { padding: 28px 34px; }
.historial-carta__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 26px;
}
.historial-carta__titulo { font-size: 28px; font-weight: 700; color: #1a202c; margin: 0; }
.historial-carta__rango { font-size: 15px; font-weight: 400; color: #6b7684; margin-left: 14px; }
/* Mas contraste que la nota gris: esto no es contexto, es una advertencia de
   que lo que se ve en pantalla NO es todo. */
.historial-carta__aviso {
  margin-top: 14px;
  padding: 10px 14px;
  border-left: 3px solid #fb6340;
  background: #fff8f5;
  border-radius: 4px;
  font-size: 12.5px;
  color: #5e4b45;
  line-height: 1.6;
}
.historial-carta__cargando { padding: 60px 0; text-align: center; color: #8a94a0; font-size: 14px; }
.historial-carta__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.historial-carta__buscar { flex: 1; min-width: 240px; max-width: 380px; margin-bottom: 0; }
.historial-carta__paginacion { margin-top: 18px; text-align: right; }
.celda-vacia { color: #c2c9d1; }

/*
  Filtros: mismos tokens que el dashboard de Operaciones (globals.css de
  dashboard-ops). Ojo — ahi el "seleccionado" es NEGRO (mv-gray-900 #171717),
  no el verde de marca; el verde queda solo para acciones primarias.
*/
.filtros {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.filtros__campo { position: relative; display: inline-flex; }
.filtros__select {
  appearance: none;
  -webkit-appearance: none;
  background: #fff;
  border: 1px solid #e8e8e8;      /* mv-gray-200 */
  border-radius: 8px;             /* rounded-lg */
  padding: 8px 30px 8px 12px;     /* px-3 py-2 + espacio del chevron */
  font-size: 14px;
  color: #404040;                 /* mv-gray-700 */
  cursor: pointer;
  transition: border-color 0.15s;
}
.filtros__select:hover { border-color: #d4d4d4; }   /* mv-gray-300 */
.filtros__select:focus { outline: none; border-color: #008846; }
.filtros__chevron {
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 10px;
  color: #a3a3a3;                 /* mv-gray-400 */
  pointer-events: none;
}
.filtros__limpiar {
  background: none;
  border: none;
  padding: 8px 6px;
  font-size: 13px;
  color: #525252;                 /* mv-gray-600 */
  cursor: pointer;
}
.filtros__limpiar:hover { color: #171717; }

.segmented {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f5f5f5;            /* mv-gray-100 */
  padding: 4px;
  border-radius: 8px;
}
.segmented__item {
  background: none;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;             /* rounded-md */
  font-size: 13px;
  font-weight: 500;
  color: #525252;                 /* mv-gray-600 */
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s, color 0.15s;
}
.segmented__item:hover { color: #171717; }
.segmented__item--activo,
.segmented__item--activo:hover {
  background: #171717;            /* mv-gray-900 */
  color: #fff;
}
.pill {
  display: inline-block;
  border-radius: 6px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 700;
}
.pill--verde { background: #e8f5ec; color: #01662e; }
.pill--roja { background: #fef2f2; color: #dc2626; }

/*
  _element-tables.scss vuelve flex el ".cell" del header (para centrarlo
  verticalmente), sin justify-content. En un contenedor flex, text-align no
  hace nada — el titulo queda pegado a la izquierda aunque header-align sea
  "center". El dato de las filas SI se centra bien (esa celda no es flex).
  No se toca el scss global: podria afectar otras tablas de la app.
*/
::v-deep .el-table__header th.is-center .cell {
  justify-content: center;
}
</style>
